import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  CategoryList,
  CategoryListSliceState,
} from '../../../types/ExpenseManagement/Category/categoryListTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import categoryListApi from '../../../middleware/api/ExpenseManagement/Category/expenseCategoryListApi'

const getCategoryList = createAsyncThunk(
  '/ExpenseManagement/getCategoryList',
  async (_, thunkApi) => {
    try {
      return await categoryListApi.getExpenseCategories()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const editExpenseCategory = createAsyncThunk(
  '/ExpenseManagement/editCategory',
  async (categoryId: number, thunkApi) => {
    try {
      return await categoryListApi.editNewCategory(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateExpenseCategory = createAsyncThunk(
  '/ExpenseManagement/updateCategory',
  async (data: CategoryList, thunkApi) => {
    try {
      return await categoryListApi.updateNewCategory(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteExpenseCategory = createAsyncThunk(
  '/ExpenseManagement/deleteCategory',
  async (categoryId: number, thunkApi) => {
    try {
      return await categoryListApi.deleteCategory(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialCategoryListState: CategoryListSliceState = {
  getAllCategory: [],
  isLoading: ApiLoadingState.idle,
  currentPage: 1,
  pageSize: 20,
  error: null,
  isLoadingNew: ApiLoadingState.idle,
}

const categoryListSlice = createSlice({
  name: 'category',
  initialState: initialCategoryListState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCategoryList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllCategory = action.payload
      })
      .addCase(updateExpenseCategory.fulfilled, (state) => {
        state.isLoadingNew = ApiLoadingState.succeeded
      })
      .addCase(updateExpenseCategory.pending, (state) => {
        state.isLoadingNew = ApiLoadingState.loading
      })
      .addCase(updateExpenseCategory.rejected, (state) => {
        state.isLoadingNew = ApiLoadingState.failed
      })
      .addCase(deleteExpenseCategory.fulfilled, (state) => {
        state.isLoadingNew = ApiLoadingState.succeeded
      })
      .addCase(deleteExpenseCategory.pending, (state) => {
        state.isLoadingNew = ApiLoadingState.loading
      })
      .addCase(deleteExpenseCategory.rejected, (state) => {
        state.isLoadingNew = ApiLoadingState.failed
      })
      .addMatcher(
        isAnyOf(editExpenseCategory.fulfilled, updateExpenseCategory.fulfilled),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          getCategoryList.pending,
          editExpenseCategory.pending,
          updateExpenseCategory.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getCategoryList.rejected,
          editExpenseCategory.rejected,
          updateExpenseCategory.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.categoryList.isLoading
const categories = (state: RootState): CategoryList[] =>
  state.categoryList.getAllCategory
const pageFromState = (state: RootState): number =>
  state.categoryList.currentPage
const pageSizeFromState = (state: RootState): number =>
  state.categoryList.pageSize
const getIsLoadingNew = (state: RootState): LoadingState =>
  state.categoryList.isLoadingNew

const categoryListThunk = {
  getCategoryList,
  editExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
}

const categoryListSelectors = {
  isLoading,
  categories,
  pageFromState,
  pageSizeFromState,
  getIsLoadingNew,
}

export const categoryListService = {
  ...categoryListThunk,
  actions: categoryListSlice.actions,
  selectors: categoryListSelectors,
}

export default categoryListSlice.reducer
